import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from '../../lib/utils';
import StoreStockPage from './StoreStockPage';

// 매장 발주요청 — 역할별 단일 메뉴
//  탭1 발주요청: 매장재고 상품별 (기간판매량 / 현재고 / 발주수량 자동) 작성 → [발주요청]
//    · 발주수량 = max(0, 직전 발주~오늘 판매량 − 현재고)  (수정/삭제/상품추가 가능)
//    · 0 수량은 전체선택해도 본사로 전송되지 않음
//  탭2 발송현황: 요청 이력 + 도착분 입고확인(재고 +반영)
export default function StockRequestPage({ profile, demo = false }) {
  const store  = profile.department;
  const branch = profile.branch;
  const today  = new Date().toISOString().slice(0, 10);

  const [tab, setTab] = useState('request'); // 'stock' | 'request' | 'status'

  // ── 발주요청 작성 ──
  const [sheetLoading, setSheetLoading] = useState(true);
  const [fromDate, setFromDate] = useState(null);   // 기간 시작(직전 발주일)
  const [rows, setRows] = useState([]);             // {productId, brandId, name, code, stock, soldQty, qty, checked}
  const [submitting, setSubmitting] = useState(false);

  // 상품추가 검색
  const [allProducts, setAllProducts] = useState([]);
  const [stockMap, setStockMap] = useState(new Map());  // code → stock_qty
  const [salesMap, setSalesMap] = useState(new Map());  // productId → soldQty
  const [addSearch, setAddSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  // 목록 내 찾기 / 0수량 숨기기
  const [listQuery, setListQuery] = useState('');
  const [hideZero, setHideZero] = useState(false);

  const loadSheet = useCallback(async () => {
    if (demo) {
      // 사용안내 따라하기용 예시 데이터
      setFromDate('2026-07-01');
      setAllProducts([]);
      setRows([
        { productId:'d1', brandId:null, brandName:'팔레오', name:'팔레오_닥터스노트 초임계rTG 알티지 오메가3', code:'8809722528425', stock:2, soldQty:9, qty:7, checked:true },
        { productId:'d2', brandId:null, brandName:'팔레오', name:'팔레오_유기농레몬즙100%',                 code:'8809956531697', stock:1, soldQty:6, qty:5, checked:true },
        { productId:'d3', brandId:null, brandName:'로엘',   name:'로엘_모유유래프롤린유산균 2g*30P',          code:'8809956530430', stock:0, soldQty:4, qty:4, checked:true },
        { productId:'d4', brandId:null, brandName:'팔레오', name:'팔레오_대마종자유캡슐',                     code:'8809722525837', stock:5, soldQty:5, qty:0, checked:false },
        { productId:'d5', brandId:null, brandName:'팔레오', name:'팔레오_닥터스노트 백옥 글루타치온정 1000mg*60T', code:'8809956531703', stock:8, soldQty:3, qty:0, checked:false },
      ]);
      setSheetLoading(false);
      return;
    }
    setSheetLoading(true);
    try {
      // 1) 매장재고
      const { data: stockRows } = await supabase.from('store_stock')
        .select('product_code, product_name, stock_qty')
        .eq('store_name', store).eq('branch_name', branch);
      const sMap = new Map();
      for (const s of (stockRows || [])) sMap.set(String(s.product_code || '').trim(), Number(s.stock_qty) || 0);

      // 2) 상품 마스터 (code/erp_code → product) — 1000행 제한 회피 위해 전량 페이징
      const prods = [];
      { let start = 0; const PAGE = 1000;
        while (true) {
          const { data, error } = await supabase.from('products')
            .select('id, code, erp_code, brand_id, name, is_sales_stopped, brand:brands(name)')
            .order('id').range(start, start + PAGE - 1);
          if (error) break;
          if (!data || data.length === 0) break;
          prods.push(...data);
          if (data.length < PAGE) break;
          start += PAGE;
        }
      }
      const codeMap = new Map();
      for (const p of (prods || [])) {
        if (p.code)     codeMap.set(String(p.code).trim(), p);
        if (p.erp_code) codeMap.set(String(p.erp_code).trim(), p);
      }

      // 3) 직전 발주요청일 → 기간 시작
      const { data: lastReq } = await supabase.from('order_requests')
        .select('created_at, request_date')
        .eq('store_name', store).eq('branch_name', branch)
        .order('created_at', { ascending: false }).limit(1);
      let from;
      if (lastReq && lastReq.length > 0) {
        from = (lastReq[0].request_date || lastReq[0].created_at || '').slice(0, 10);
      }
      if (!from) { const d = new Date(); d.setDate(d.getDate() - 30); from = d.toISOString().slice(0, 10); }

      // 4) 기간 판매량 (product_id 합)
      const { data: salesRows } = await supabase.from('sales')
        .select('product_id, quantity, sold_at')
        .neq('payment', '구매이력')
        .eq('store_name', store).eq('branch_name', branch)
        .gte('sold_at', from);
      const saMap = new Map();
      for (const r of (salesRows || [])) {
        const pid = String(r.product_id);
        saMap.set(pid, (saMap.get(pid) || 0) + (Number(r.quantity) || 0));
      }

      // 5) 발주 시트 — 매장재고 등록 상품 기준 (판매중지 제외)
      const list = [];
      const seen = new Set();
      for (const s of (stockRows || [])) {
        const code = String(s.product_code || '').trim();
        const p = codeMap.get(code);
        if (!p || p.is_sales_stopped) continue;        // 미등록/판매중지 제외
        if (seen.has(p.id)) continue; seen.add(p.id);
        const stock = sMap.get(code) || 0;
        const soldQty = saMap.get(String(p.id)) || 0;
        const qty = Math.max(0, soldQty - stock);
        list.push({ productId: p.id, brandId: p.brand_id, name: p.name, code,
          brandName: p.brand?.name || '', stock, soldQty, qty, checked: qty > 0 });
      }
      list.sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));

      setStockMap(sMap); setSalesMap(saMap); setAllProducts(prods || []);
      setFromDate(from); setRows(list);
    } catch (err) {
      toast('발주 시트 로드 실패: ' + (err.message || err), 'err');
    }
    setSheetLoading(false);
  }, [store, branch, demo]);

  useEffect(() => { if (tab === 'request') loadSheet(); }, [tab, loadSheet]);

  const setRowQty = (productId, val) => setRows(prev => prev.map(r =>
    r.productId === productId ? { ...r, qty: val } : r));
  const setRowChecked = (productId, val) => setRows(prev => prev.map(r =>
    r.productId === productId ? { ...r, checked: val } : r));
  const removeRow = (productId) => setRows(prev => prev.filter(r => r.productId !== productId));

  // 화면 표시용(찾기/0숨기기 적용) — 실제 rows/발주 로직에는 영향 없음
  const visibleRows = rows.filter(r => {
    if (hideZero && (Number(r.qty) || 0) <= 0) return false;
    const q = listQuery.trim().toLowerCase();
    if (!q) return true;
    return (r.name || '').toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q);
  });

  const allChecked = visibleRows.length > 0 && visibleRows.every(r => r.checked);
  const toggleAll = () => {
    const v = !allChecked;
    const ids = new Set(visibleRows.map(r => r.productId));
    setRows(prev => prev.map(r => ids.has(r.productId) ? { ...r, checked: v } : r));
  };

  const SUGGEST_LIMIT = 50;
  const addMatches = (() => {
    const q = addSearch.trim().toLowerCase();
    if (!q) return [];
    const inSheet = new Set(rows.map(r => r.productId));
    return allProducts
      .filter(p => !p.is_sales_stopped)                       // 판매중지만 제외 (판매중은 모두 노출)
      .filter(p =>
        (p.name || '').toLowerCase().includes(q)
        || (p.code || '').toLowerCase().includes(q)
        || (p.erp_code || '').toLowerCase().includes(q))
      .map(p => ({ ...p, _inSheet: inSheet.has(p.id) }))      // 이미 목록에 있는지 표시(숨기지 않음)
      .sort((a, b) => {
        if (a._inSheet !== b._inSheet) return a._inSheet ? 1 : -1; // 아직 없는 것 먼저
        const an = (a.name || '').toLowerCase(), bn = (b.name || '').toLowerCase();
        const as = an.startsWith(q) ? 0 : 1, bs = bn.startsWith(q) ? 0 : 1; // 이름 시작 일치 우선
        return as - bs || an.localeCompare(bn);
      });
  })();
  const addSuggestions = addMatches.slice(0, SUGGEST_LIMIT);

  const addProduct = (p) => {
    if (rows.some(r => r.productId === p.id)) { // 이미 목록에 있으면 추가 안 함
      toast('이미 발주 목록에 있는 상품입니다', 'inf');
      setAddSearch(''); setShowAdd(false);
      return;
    }
    const code = String(p.code || '').trim();
    const stock = stockMap.get(code) || 0;
    const soldQty = salesMap.get(String(p.id)) || 0;
    setRows(prev => [{ productId: p.id, brandId: p.brand_id, name: p.name, code,
      brandName: p.brand?.name || '', stock, soldQty, qty: Math.max(0, soldQty - stock), checked: true }, ...prev]);
    setAddSearch(''); setShowAdd(false);
  };

  const submit = async () => {
    const targets = rows.filter(r => r.checked && (Number(r.qty) || 0) > 0);
    if (targets.length === 0) { toast('발주수량이 1개 이상인 상품을 선택해주세요', 'err'); return; }
    if (!window.confirm(`${targets.length}개 상품을 본사에 발주요청하시겠습니까?\n(수량 0인 상품은 제외됩니다)`)) return;
    setSubmitting(true);
    try {
      const batchId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
      const payload = targets.map(r => ({
        store_name: store, branch_name: branch,
        brand_id: r.brandId ? Number(r.brandId) : null,
        product_id: Number(r.productId),
        quantity: Number(r.qty),
        status: 'pending',
        batch_id: batchId,
        request_date: today,
        safety_qty: r.soldQty,
        current_qty: r.stock,
        created_by: profile.id,
        requested_by: profile.id,
      }));
      const { error } = await supabase.from('order_requests').insert(payload);
      if (error) throw error;
      toast(`발주요청 완료 — ${targets.length}건`, 'ok');
      setTab('status');
    } catch (err) {
      toast('발주요청 실패: ' + (err.message || err), 'err');
    }
    setSubmitting(false);
  };

  // ── 발송현황 (요청 이력 + 입고확인) ──
  const [requests, setRequests] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [receiving, setReceiving] = useState({});
  const [scanTarget, setScanTarget] = useState(null); // 입고확인 바코드 스캔 대상 row
  const [scanCode, setScanCode] = useState('');
  const [editReq, setEditReq]   = useState({}); // 대기 요청 수량 인라인 수정 {id: '값'}
  const [savingReq, setSavingReq] = useState({});

  const fetchRequests = useCallback(async () => {
    setHistLoading(true);
    const { data } = await supabase.from('order_requests')
      .select('*, brand:brands(name), product:products(name, code, erp_code)')
      .eq('store_name', store).eq('branch_name', branch)
      .order('created_at', { ascending: false })
      .limit(200);
    setRequests(data || []);
    setHistLoading(false);
  }, [store, branch]);

  useEffect(() => { if (tab === 'status') fetchRequests(); }, [tab, fetchRequests]);

  // 대기 상태 발주요청 수량 수정 (본사 발송요청 전까지만 가능)
  const saveReqQty = async (r) => {
    const next = Number(editReq[r.id]);
    if (!Number.isFinite(next) || next < 1) { toast('수량은 1 이상으로 입력해주세요', 'err'); return; }
    setSavingReq(p => ({ ...p, [r.id]: true }));
    // 본사가 이미 발송요청했으면 차단 (동시성 보호)
    const { data: cur } = await supabase.from('order_requests').select('status').eq('id', r.id).maybeSingle();
    if (cur && cur.status !== 'pending') {
      toast('본사가 이미 발송요청하여 수정할 수 없습니다', 'err');
      setSavingReq(p => { const n = { ...p }; delete n[r.id]; return n; });
      fetchRequests();
      return;
    }
    const { error } = await supabase.from('order_requests')
      .update({ quantity: next, updated_at: new Date().toISOString() })
      .eq('id', r.id).eq('status', 'pending'); // pending일 때만 반영
    if (error) toast(error.message, 'err');
    else {
      toast(`수량 ${r.quantity} → ${next} 수정 완료`, 'ok');
      setEditReq(p => { const n = { ...p }; delete n[r.id]; return n; });
      fetchRequests();
    }
    setSavingReq(p => { const n = { ...p }; delete n[r.id]; return n; });
  };
  const cancelReq = async (r) => {
    if (!window.confirm(`'${r.product?.name}' 발주요청을 취소(삭제)하시겠습니까?`)) return;
    setSavingReq(p => ({ ...p, [r.id]: true }));
    const { error } = await supabase.from('order_requests').delete().eq('id', r.id).eq('status', 'pending');
    if (error) toast(error.message, 'err');
    else { toast('발주요청 취소 완료', 'inf'); fetchRequests(); }
    setSavingReq(p => { const n = { ...p }; delete n[r.id]; return n; });
  };

  // 입고확인: 바코드 스캔 모달 → 코드 일치 시 입고
  const openScan = (r) => { setScanTarget(r); setScanCode(''); };
  const confirmScan = async () => {
    if (!scanTarget) return;
    const expected = [scanTarget.product?.code, scanTarget.product?.erp_code]
      .filter(Boolean).map(c => String(c).trim().toLowerCase());
    const scanned = scanCode.trim().toLowerCase();
    if (!scanned) { toast('바코드를 스캔해주세요', 'err'); return; }
    if (expected.length && !expected.includes(scanned)) {
      toast(`바코드 불일치 — 다른 상품입니다 (예상: ${scanTarget.product?.code || '-'})`, 'err');
      setScanCode('');
      return;
    }
    const r = scanTarget;
    setScanTarget(null);
    await doReceive(r);
  };

  const doReceive = async (r) => {
    setReceiving(prev => ({ ...prev, [r.id]: true }));
    try {
      const code = r.product?.code;
      if (!code) throw new Error('상품 코드 누락 — 본사에 문의해주세요');
      const { data: stockRow } = await supabase.from('store_stock')
        .select('id, stock_qty').eq('store_name', store).eq('branch_name', branch)
        .eq('product_code', code).maybeSingle();
      if (stockRow) {
        const { error } = await supabase.from('store_stock').update({
          stock_qty: (stockRow.stock_qty || 0) + Number(r.quantity), updated_at: new Date().toISOString(),
        }).eq('id', stockRow.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('store_stock').insert({
          store_name: store, branch_name: branch, product_code: code,
          product_name: r.product.name, stock_qty: Number(r.quantity), updated_at: new Date().toISOString(),
        });
        if (error) throw error;
      }
      const { error: updErr } = await supabase.from('order_requests').update({
        status: 'received', received_at: new Date().toISOString(), received_by: profile.id,
      }).eq('id', r.id);
      if (updErr) throw updErr;
      toast(`입고 처리 완료 — 매장 재고에 +${r.quantity}개 반영`, 'ok');
      fetchRequests();
    } catch (err) {
      toast('입고 실패: ' + (err.message || err), 'err');
    }
    setReceiving(prev => { const n = { ...prev }; delete n[r.id]; return n; });
  };

  // ── 매장 재고이동 (다른 매장에서 온 점간이동 입고) ──
  const [transfers, setTransfers]       = useState([]);   // 도착 대기 (dispatched)
  const [recvTransfers, setRecvTransfers] = useState([]); // 최근 입고완료 (received)
  const [txLoading, setTxLoading]       = useState(true);
  const [txScan, setTxScan]             = useState(null); // 입고확인(스캔) 중인 transfer
  const [txScanCount, setTxScanCount]   = useState(0);    // 스캔된 수량
  const [txScanInput, setTxScanInput]   = useState('');
  const [txSaving, setTxSaving]         = useState(false);

  // 출고일 2026-09-01(KST) 이후 재고이동만 노출 (그 이전 이력은 제외)
  const TRANSFER_FROM = '2026-08-31T15:00:00Z';
  const fetchTransfers = useCallback(async () => {
    if (demo) { setTransfers([]); setRecvTransfers([]); setTxLoading(false); return; }
    setTxLoading(true);
    const { data: pend } = await supabase.from('store_transfers')
      .select('*, product:products(name, code, erp_code)')
      .eq('to_store_name', store).eq('to_branch_name', branch)
      .eq('status', 'dispatched')
      .gte('dispatched_at', TRANSFER_FROM)
      .order('dispatched_at', { ascending: false });
    setTransfers(pend || []);
    const { data: rcv } = await supabase.from('store_transfers')
      .select('*, product:products(name, code, erp_code)')
      .eq('to_store_name', store).eq('to_branch_name', branch)
      .eq('status', 'received')
      .gte('dispatched_at', TRANSFER_FROM)
      .order('received_at', { ascending: false }).limit(20);
    setRecvTransfers(rcv || []);
    setTxLoading(false);
  }, [store, branch, demo]);

  useEffect(() => { if (tab === 'transfer') fetchTransfers(); }, [tab, fetchTransfers]);

  const openTxScan  = (t) => { setTxScan(t); setTxScanCount(0); setTxScanInput(''); };
  const closeTxScan = () => { setTxScan(null); setTxScanCount(0); setTxScanInput(''); };

  // 바코드 스캔 1회 = 코드 검증 후 스캔 수량 +1 (보낸 수량 초과 방지)
  const onTxScanEnter = () => {
    if (!txScan) return;
    const expected = [txScan.product?.code, txScan.product?.erp_code]
      .filter(Boolean).map(c => String(c).trim().toLowerCase());
    const scanned = txScanInput.trim().toLowerCase();
    setTxScanInput('');
    if (!scanned) return;
    if (expected.length && !expected.includes(scanned)) {
      toast(`바코드 불일치 — 다른 상품입니다 (예상: ${txScan.product?.code || '-'})`, 'err');
      return;
    }
    setTxScanCount(c => {
      const next = c + 1;
      if (next > (Number(txScan.quantity) || 0)) {
        toast(`보낸 수량(${txScan.quantity}개)을 초과할 수 없습니다`, 'err');
        return c;
      }
      toast(`📷 +1 (${next}/${txScan.quantity})`, 'ok');
      return next;
    });
  };

  const confirmTxReceive = async () => {
    if (!txScan) return;
    const qty  = Number(txScanCount) || 0;
    const sent = Number(txScan.quantity) || 0;
    if (qty <= 0) { toast('스캔된 수량이 없습니다 — 바코드를 스캔해주세요', 'err'); return; }
    if (qty < sent && !window.confirm(`보낸 수량 ${sent}개 중 ${qty}개만 입고됩니다.\n부족분 ${sent - qty}개는 보낸 매장(${txScan.from_store_name} ${txScan.from_branch_name}) 재고로 복원됩니다. 진행할까요?`)) return;
    setTxSaving(true);
    try {
      const code = txScan.product?.code;
      if (!code) throw new Error('상품 코드 누락 — 본사에 문의해주세요');
      const { data: stockRow } = await supabase.from('store_stock')
        .select('id, stock_qty').eq('store_name', store).eq('branch_name', branch)
        .eq('product_code', code).maybeSingle();
      if (stockRow) {
        const { error } = await supabase.from('store_stock').update({
          stock_qty: (stockRow.stock_qty || 0) + qty, updated_at: new Date().toISOString(),
        }).eq('id', stockRow.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('store_stock').insert({
          store_name: store, branch_name: branch,
          product_name: txScan.product?.name || null,
          product_code: code, stock_qty: qty, updated_at: new Date().toISOString(),
        });
        if (error) throw error;
      }
      // dispatched 상태일 때만 반영 (중복 입고 방지)
      const { error: updErr } = await supabase.from('store_transfers').update({
        status: 'received', received_qty: qty,
        received_at: new Date().toISOString(), received_by: profile.id,
        updated_at: new Date().toISOString(),
      }).eq('id', txScan.id).eq('status', 'dispatched');
      if (updErr) throw updErr;

      // 부족 입고분은 보낸 매장 재고로 복원 (보낸 매장은 출고 시 이미 -sent 되었으므로
      // 실제 도착이 적으면 그만큼 되돌려 실질 차감 = 받은 수량이 되도록 한다)
      const shortfall = sent - qty;
      if (shortfall > 0 && code) {
        const { data: srcRow } = await supabase.from('store_stock')
          .select('id, stock_qty')
          .eq('store_name', txScan.from_store_name)
          .eq('branch_name', txScan.from_branch_name)
          .eq('product_code', code).maybeSingle();
        if (srcRow) {
          const { error: e } = await supabase.from('store_stock').update({
            stock_qty: (srcRow.stock_qty || 0) + shortfall, updated_at: new Date().toISOString(),
          }).eq('id', srcRow.id);
          if (e) throw e;
        } else {
          const { error: e } = await supabase.from('store_stock').insert({
            store_name: txScan.from_store_name, branch_name: txScan.from_branch_name,
            product_name: txScan.product?.name || null,
            product_code: code, stock_qty: shortfall, updated_at: new Date().toISOString(),
          });
          if (e) throw e;
        }
      }

      toast(shortfall > 0
        ? `입고완료 — ${store} +${qty}개 / 부족분 ${shortfall}개 ${txScan.from_store_name} ${txScan.from_branch_name} 재고로 복원`
        : `입고완료 — 매장 재고에 +${qty}개 반영`, 'ok');
      closeTxScan();
      fetchTransfers();
    } catch (err) {
      toast('입고 실패: ' + (err.message || err), 'err');
    } finally {
      setTxSaving(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending:       { t:'⏳ 요청대기',   bg:'#fff3e0', c:'#E65100' },
      scm_requested: { t:'🚚 발송준비',   bg:'#e3f2fd', c:'#1565C0' },
      shipped:       { t:'📦 발송완료',   bg:'#f3e5f5', c:'#6a1b9a' },
      received:      { t:'✅ 입고완료',   bg:'#e8f5e9', c:'#2e7d32' },
      ordered:       { t:'📦 발주진행중', bg:'#f3e5f5', c:'#6a1b9a' },
      fulfilled:     { t:'✅ 자체완료',   bg:'#e8f5e9', c:'#2e7d32' },
      rejected:      { t:'본사반려',      bg:'#ffebee', c:'#c62828' },
    };
    const m = map[s] || { t:s, bg:'#f5f5f5', c:'#666' };
    return <span style={{ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600, background:m.bg, color:m.c }}>{m.t}</span>;
  };

  const totalReqQty = rows.filter(r => r.checked && (Number(r.qty)||0) > 0).reduce((s, r) => s + (Number(r.qty)||0), 0);

  const inputStyle = { width:'100%', height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fff', fontSize:13, outline:'none' };

  return (
    <div>
      <div className="tabbar" style={{ display:'flex', gap:4, marginBottom:14 }}>
        <button className={`tab ${tab==='stock'?'on':''}`} onClick={() => setTab('stock')}>📊 재고현황</button>
        <button className={`tab ${tab==='request'?'on':''}`} onClick={() => setTab('request')}>📝 발주요청</button>
        <button className={`tab ${tab==='status'?'on':''}`} onClick={() => setTab('status')}>📥 입고확인</button>
        <button className={`tab ${tab==='transfer'?'on':''}`} onClick={() => setTab('transfer')}>🔁 매장 재고이동</button>
      </div>

      {/* ── 재고현황 ── */}
      {tab === 'stock' && <StoreStockPage profile={profile}/>}

      {/* ── 발주요청 ── */}
      {tab === 'request' && (
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexWrap:'wrap', gap:8 }}>
            <div>
              <div className="card-label" style={{ margin:0 }}>📝 발주요청 작성</div>
              <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>
                📍 {store} · {branch}
                {fromDate && <span style={{ marginLeft:10, color:'var(--text3)' }}>판매기간: <b>{fromDate} ~ {today}</b> (직전 발주 이후)</span>}
              </div>
            </div>
            <div style={{ position:'relative' }}>
              <input data-tour="req-add" value={addSearch} onChange={e => { setAddSearch(e.target.value); setShowAdd(true); }}
                onFocus={() => setShowAdd(true)} onBlur={() => setTimeout(() => setShowAdd(false), 200)}
                placeholder="➕ 상품 추가 검색" style={{ ...inputStyle, width:240 }} autoComplete="off" />
              {showAdd && addSuggestions.length > 0 && (
                <div style={{ position:'absolute', top:'100%', right:0, zIndex:100, width:300, background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.12)', maxHeight:240, overflowY:'auto' }}>
                  {addSuggestions.map(p => (
                    <div key={p.id} onMouseDown={e => { e.preventDefault(); addProduct(p); }}
                      style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid #f0f0f0', opacity: p._inSheet ? 0.55 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background='#fffde7'}
                      onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                      {p.brand?.name && <span style={{ fontSize:11, color:'var(--accent)', fontWeight:700, marginRight:6 }}>[{p.brand.name}]</span>}
                      {p.name}
                      {p.code && <span style={{ fontSize:10, color:'var(--text3)', marginLeft:6, fontFamily:'var(--mono)' }}>{p.code}</span>}
                      {p._inSheet && <span style={{ fontSize:10, color:'#1565C0', marginLeft:6, fontWeight:700 }}>✓ 이미 목록에 있음</span>}
                    </div>
                  ))}
                  {addMatches.length > SUGGEST_LIMIT && (
                    <div style={{ padding:'8px 12px', fontSize:11, color:'var(--text3)', background:'#fafafa' }}>
                      상위 {SUGGEST_LIMIT}개만 표시 · 검색어를 더 구체적으로 입력하세요 (총 {addMatches.length}개)
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {sheetLoading ? <div className="empty"><span className="spinner"/></div>
          : rows.length === 0 ? <div className="empty">매장재고에 등록된 상품이 없습니다 (상품 추가로 직접 담을 수 있습니다)</div>
          : (
            <>
            {/* 목록 내 찾기 */}
            <div data-tour="req-find" style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10, flexWrap:'wrap' }}>
              <div style={{ position:'relative', width:260 }}>
                <input value={listQuery} onChange={e => setListQuery(e.target.value)}
                  placeholder="🔍 목록에서 상품 찾기 (상품명/코드)" style={{ ...inputStyle }} />
                {listQuery && (
                  <button type="button" onClick={() => setListQuery('')}
                    style={{ position:'absolute', right:6, top:'50%', transform:'translateY(-50%)', border:'none', background:'none', cursor:'pointer', color:'var(--text3)', fontSize:14 }}>✕</button>
                )}
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text2)', cursor:'pointer' }}>
                <input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} style={{ cursor:'pointer' }}/>
                발주수량 0 숨기기
              </label>
              <span style={{ fontSize:12, color:'var(--text3)' }}>표시 <b>{visibleRows.length}</b> / 전체 {rows.length}개</span>
            </div>

            {visibleRows.length === 0 ? (
              <div className="empty">조건에 맞는 상품이 없습니다 {listQuery && `("${listQuery}")`}</div>
            ) : (
            <div className="twrap" data-tour="req-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36, textAlign:'center' }}>
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{ cursor:'pointer' }}/>
                    </th>
                    <th>상품명</th>
                    <th className="r" style={{ width:110 }}>기간 판매량</th>
                    <th className="r" style={{ width:90 }}>현재고</th>
                    <th className="r" style={{ width:120 }}>발주수량</th>
                    <th style={{ width:50, textAlign:'center' }}>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => (
                    <tr key={r.productId} style={{ background: r.checked ? '' : '#fafafa' }}>
                      <td style={{ textAlign:'center' }}>
                        <input type="checkbox" checked={r.checked} onChange={e => setRowChecked(r.productId, e.target.checked)} style={{ cursor:'pointer' }}/>
                      </td>
                      <td style={{ fontSize:13 }}>
                        {r.brandName && <span style={{ fontSize:11, color:'var(--accent)', fontWeight:700, marginRight:6 }}>[{r.brandName}]</span>}
                        {r.name}
                        {r.code && <span style={{ fontSize:10, color:'var(--text3)', marginLeft:6, fontFamily:'var(--mono)' }}>{r.code}</span>}
                      </td>
                      <td className="r" style={{ fontFamily:'var(--mono)', color:'var(--text2)' }}>{r.soldQty.toLocaleString()}</td>
                      <td className="r" style={{ fontFamily:'var(--mono)', color: r.stock === 0 ? 'var(--danger)' : 'var(--text)' }}>{r.stock.toLocaleString()}</td>
                      <td className="r">
                        <input type="number" min={0} value={r.qty}
                          onChange={e => setRowQty(r.productId, e.target.value)}
                          style={{ width:90, height:30, padding:'0 8px', border:`1px solid ${(Number(r.qty)||0) > 0 ? 'var(--accent)' : 'var(--border)'}`, borderRadius:4, fontFamily:'var(--mono)', fontWeight:700, textAlign:'right', fontSize:13, background:(Number(r.qty)||0) > 0 ? '#fff3e0' : '#fff' }}/>
                      </td>
                      <td style={{ textAlign:'center' }}>
                        <button type="button" onClick={() => removeRow(r.productId)} title="행 삭제"
                          style={{ padding:'2px 8px', fontSize:12, border:'1px solid var(--border)', borderRadius:4, background:'#fff', color:'var(--danger)', cursor:'pointer' }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:14, flexWrap:'wrap', gap:10 }}>
              <span style={{ fontSize:13, color:'var(--text2)' }}>
                선택 <b>{rows.filter(r => r.checked && (Number(r.qty)||0) > 0).length}</b>개 상품 · 총 <b style={{ color:'var(--accent)' }}>{totalReqQty.toLocaleString()}</b>개
              </span>
              <button className="btn btn-p" onClick={submit} disabled={submitting} data-tour="req-submit"
                style={{ height:42, padding:'0 28px', fontSize:14, fontWeight:700 }}>
                {submitting ? <span className="spinner"/> : '📦 발주요청'}
              </button>
            </div>
            <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>
              · 발주수량 = 기간 판매량 − 현재고 (자동, 수정 가능) · 수량 0인 상품은 전체선택해도 본사로 전송되지 않습니다
            </div>
            </>
          )}
        </div>
      )}

      {/* ── 발송현황 ── */}
      {tab === 'status' && (
        <div className="card">
          <div className="card-label">📥 입고확인 / 발송현황</div>
          {histLoading ? <div className="empty"><span className="spinner"/></div> : (
            <div className="twrap">
              <table>
                <thead>
                  <tr><th>요청일</th><th>브랜드</th><th>상품명</th><th className="r">수량</th><th>상태</th><th>송장</th><th style={{ width:160, textAlign:'center' }}>수정/입고</th></tr>
                </thead>
                <tbody>
                  {requests.length === 0
                    ? <tr><td colSpan={7} className="empty">요청 내역이 없습니다</td></tr>
                    : requests.map(r => {
                      const isPending = r.status === 'pending';
                      const editing = editReq[r.id] !== undefined;
                      return (
                      <tr key={r.id}>
                        <td className="mono" style={{ fontSize:11 }}>{r.request_date || (r.created_at||'').slice(0,10)}</td>
                        <td><span className="badge badge-dept">{r.brand?.name || '-'}</span></td>
                        <td style={{ fontSize:12 }}>{r.product?.name || '-'}</td>
                        <td className="r">
                          {isPending && editing ? (
                            <input type="number" min={1} value={editReq[r.id]} autoFocus
                              onChange={e => setEditReq(p => ({ ...p, [r.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveReqQty(r); }}
                              style={{ width:64, height:28, padding:'0 6px', border:'1px solid var(--accent)', borderRadius:4, textAlign:'right', fontFamily:'var(--mono)', fontWeight:700, fontSize:13 }}/>
                          ) : (
                            <span style={{ fontWeight:700, color:'var(--accent)' }}>{r.quantity}</span>
                          )}
                        </td>
                        <td>
                          {statusBadge(r.status)}
                          {r.status === 'rejected' && r.reject_reason && (
                            <div style={{ fontSize:10, color:'var(--danger)', marginTop:3 }}>사유: {r.reject_reason}</div>
                          )}
                        </td>
                        <td style={{ fontSize:11 }}>
                          {r.tracking_number
                            ? <button type="button" onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${r.tracking_number}`, '_blank', 'noopener,noreferrer')}
                                style={{ border:'none', background:'none', color:'#1565C0', fontWeight:700, cursor:'pointer', fontFamily:'var(--mono)' }}>📦 {r.tracking_number}</button>
                            : <span style={{ color:'var(--text3)' }}>-</span>}
                        </td>
                        <td style={{ textAlign:'center' }}>
                          {isPending ? (
                            <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
                              {editing ? (
                                <>
                                  <button type="button" onClick={() => saveReqQty(r)} disabled={savingReq[r.id]}
                                    style={{ height:26, padding:'0 10px', border:'1px solid var(--accent)', borderRadius:4, background:'var(--accent)', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                                    {savingReq[r.id] ? '…' : '저장'}</button>
                                  <button type="button" onClick={() => setEditReq(p => { const n={...p}; delete n[r.id]; return n; })}
                                    style={{ height:26, padding:'0 8px', border:'1px solid var(--border)', borderRadius:4, background:'#fff', fontSize:11, cursor:'pointer' }}>취소</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" onClick={() => setEditReq(p => ({ ...p, [r.id]: String(r.quantity) }))}
                                    title="수량 수정 (본사 발송요청 전까지)"
                                    style={{ height:26, padding:'0 10px', border:'1px solid var(--accent)', borderRadius:4, background:'#fff3e0', color:'var(--accent)', fontSize:11, fontWeight:700, cursor:'pointer' }}>✏️ 수정</button>
                                  <button type="button" onClick={() => cancelReq(r)} disabled={savingReq[r.id]}
                                    title="발주요청 취소"
                                    style={{ height:26, padding:'0 8px', border:'1px solid var(--border)', borderRadius:4, background:'#fff', color:'var(--danger)', fontSize:11, fontWeight:700, cursor:'pointer' }}>✕</button>
                                </>
                              )}
                            </div>
                          ) : (r.status === 'shipped' || r.status === 'ordered') ? (
                            <button type="button" onClick={() => openScan(r)} disabled={receiving[r.id]}
                              style={{ height:28, padding:'0 12px', border:'1px solid var(--success)', borderRadius:4, background:'#e8f5e9', color:'var(--success)', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                              {receiving[r.id] ? <span className="spinner"/> : '📥 입고확인'}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── 매장 재고이동 (점간이동 입고) ── */}
      {tab === 'transfer' && (
        <div className="card">
          <div className="card-label">🔁 매장 재고이동 — 입고확인</div>
          <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
            📍 {store} · {branch} · 다른 매장에서 이 매장으로 보낸 재고이동입니다. 실제 물품 도착 후 <b>[입고확인]</b> → 바코드 스캔한 수량만큼 재고에 반영됩니다.
          </div>

          <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>📥 도착 대기 ({transfers.length}건)</div>
          {txLoading ? <div className="empty"><span className="spinner"/></div>
            : transfers.length === 0 ? <div className="empty">도착 대기 중인 재고이동이 없습니다</div>
            : (
            <div className="twrap" style={{ marginBottom:22 }}>
              <table>
                <thead>
                  <tr>
                    <th>출고일</th><th>보낸 매장</th><th>상품</th>
                    <th className="r" style={{ width:80 }}>수량</th><th>메모</th>
                    <th style={{ textAlign:'center', width:120 }}>입고확인</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(t => (
                    <tr key={t.id}>
                      <td className="mono" style={{ fontSize:11 }}>{t.dispatched_at ? new Date(t.dispatched_at).toLocaleDateString('ko-KR') : '-'}</td>
                      <td><span className="badge badge-dept">{t.from_store_name}</span> <span className="badge badge-store">{t.from_branch_name}</span></td>
                      <td style={{ fontSize:12 }}>
                        {t.product?.name || '-'}
                        {t.product?.code && <span style={{ fontSize:10, color:'var(--text3)', marginLeft:6, fontFamily:'var(--mono)' }}>{t.product.code}</span>}
                      </td>
                      <td className="r" style={{ fontFamily:'var(--mono)', fontWeight:700, color:'var(--accent)' }}>{t.quantity}개</td>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>{t.memo || '-'}</td>
                      <td style={{ textAlign:'center' }}>
                        <button type="button" onClick={() => openTxScan(t)} disabled={txSaving}
                          style={{ height:28, padding:'0 12px', border:'1px solid var(--success)', borderRadius:4, background:'#e8f5e9', color:'var(--success)', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                          📥 입고확인
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>✅ 최근 입고완료 (최근 20건)</div>
          {recvTransfers.length === 0 ? <div className="empty">입고완료 이력이 없습니다</div>
            : (
            <div className="twrap">
              <table>
                <thead>
                  <tr>
                    <th>입고일</th><th>보낸 매장</th><th>상품</th>
                    <th className="r" style={{ width:120 }}>입고/보낸</th><th>메모</th>
                  </tr>
                </thead>
                <tbody>
                  {recvTransfers.map(t => {
                    const rq = t.received_qty != null ? Number(t.received_qty) : Number(t.quantity);
                    const short = rq < Number(t.quantity);
                    return (
                      <tr key={t.id}>
                        <td className="mono" style={{ fontSize:11 }}>{t.received_at ? new Date(t.received_at).toLocaleDateString('ko-KR') : '-'}</td>
                        <td><span className="badge badge-dept">{t.from_store_name}</span> <span className="badge badge-store">{t.from_branch_name}</span></td>
                        <td style={{ fontSize:12 }}>{t.product?.name || '-'}</td>
                        <td className="r" style={{ fontFamily:'var(--mono)' }}>
                          <b style={{ color: short ? 'var(--danger)' : 'var(--text)' }}>{rq}</b> / {t.quantity}개
                          {short && <span style={{ fontSize:10, color:'var(--danger)', marginLeft:4 }}>부족</span>}
                        </td>
                        <td style={{ fontSize:11, color:'var(--text3)' }}>{t.memo || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 재고이동 입고 — 바코드 스캔(수량 카운트) 모달 */}
      {txScan && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={closeTxScan}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)' }}/>
          <div style={{ position:'relative', background:'#fff', borderRadius:12, width:'min(440px, 92vw)', padding:'22px 24px', boxShadow:'0 8px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:6 }}>🔁 재고이동 입고 — 바코드 스캔</div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>
              <strong>{txScan.product?.name}</strong>
              <span style={{ marginLeft:8, color:'var(--text3)' }}>({txScan.from_store_name} {txScan.from_branch_name} → 보낸 수량 {txScan.quantity}개)</span>
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14, fontFamily:'var(--mono)' }}>
              상품코드: {txScan.product?.code || '-'}
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, margin:'6px 0 14px' }}>
              <button type="button" onClick={() => setTxScanCount(c => Math.max(0, c - 1))}
                style={{ width:36, height:36, border:'1px solid var(--border)', borderRadius:8, background:'#fff', fontSize:18, fontWeight:700, cursor:'pointer' }}>−</button>
              <div style={{ minWidth:120, textAlign:'center' }}>
                <div style={{ fontSize:30, fontWeight:800, fontFamily:'var(--mono)', lineHeight:1, color: txScanCount > 0 ? 'var(--success)' : 'var(--text3)' }}>
                  {txScanCount}<span style={{ fontSize:15, color:'var(--text3)', fontWeight:600 }}> / {txScan.quantity}</span>
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>스캔 수량</div>
              </div>
              <button type="button" onClick={() => setTxScanCount(c => Math.min(Number(txScan.quantity) || 0, c + 1))}
                style={{ width:36, height:36, border:'1px solid var(--border)', borderRadius:8, background:'#fff', fontSize:18, fontWeight:700, cursor:'pointer' }}>＋</button>
            </div>

            <input autoFocus value={txScanInput}
              onChange={e => setTxScanInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onTxScanEnter(); } }}
              placeholder="바코드 스캔 (스캔할 때마다 +1)"
              style={{ width:'100%', height:42, padding:'0 12px', border:'2px solid var(--accent)', borderRadius:'var(--radius)', fontSize:14, fontFamily:'var(--mono)', outline:'none' }}/>
            <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>
              바코드를 스캔하면 자동으로 수량이 올라갑니다. 스캔한 수량만큼 매장 재고에 +반영됩니다. (±버튼으로 수동 조정 가능)
            </div>

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button type="button" onClick={closeTxScan} disabled={txSaving}
                style={{ height:38, padding:'0 18px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>취소</button>
              <button type="button" onClick={confirmTxReceive} disabled={txSaving || txScanCount <= 0}
                style={{ height:38, padding:'0 18px', border:'none', borderRadius:'var(--radius)', background: txScanCount > 0 ? 'var(--success)' : '#ccc', color:'#fff', fontSize:13, fontWeight:700, cursor: txScanCount > 0 ? 'pointer' : 'not-allowed' }}>
                {txSaving ? <span className="spinner"/> : `입고확인 (${txScanCount}개)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 입고확인 바코드 스캔 모달 */}
      {scanTarget && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setScanTarget(null)}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)' }}/>
          <div style={{ position:'relative', background:'#fff', borderRadius:12, width:'min(420px, 92vw)', padding:'22px 24px', boxShadow:'0 8px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:6 }}>📥 입고확인 — 바코드 스캔</div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>
              <strong>{scanTarget.product?.name}</strong> · {scanTarget.quantity}개
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14, fontFamily:'var(--mono)' }}>
              상품코드: {scanTarget.product?.code || '-'}
            </div>
            <input autoFocus value={scanCode}
              onChange={e => setScanCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmScan(); }}
              placeholder="바코드 스캔 또는 코드 입력 후 Enter"
              style={{ width:'100%', height:42, padding:'0 12px', border:'2px solid var(--accent)', borderRadius:'var(--radius)', fontSize:14, fontFamily:'var(--mono)', outline:'none' }}/>
            <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>
              스캔한 코드가 상품과 일치하면 매장 재고에 +{scanTarget.quantity}개 반영됩니다.
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button type="button" onClick={() => setScanTarget(null)}
                style={{ height:38, padding:'0 18px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>취소</button>
              <button type="button" onClick={confirmScan}
                style={{ height:38, padding:'0 18px', border:'none', borderRadius:'var(--radius)', background:'var(--success)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>입고확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
